import { StyleOptions } from "./interfaces";
import getDecorators from "inversify-inject-decorators";
import container from "../../ioc/inversify.config";
import TYPES from "../../ioc/types";
import { ThemeManager } from "../design-system/theme-manager";

const { lazyInject } = getDecorators(container);

export class Layout {
    @lazyInject(TYPES.ThemeManager)
    protected themeManager: ThemeManager;
}

export class VerticalLayout extends Layout {
    /**
     * Center elements horizontally with optional padding
     *
     * @param parentWidth
     * @param containers
     * @param styleOptions
     */
    public centerX(
        parentWidth: number,
        containers: Phaser.GameObjects.Container[],
        styleOptions?: StyleOptions
    ) {
        const paddingLeft = styleOptions && styleOptions.paddingLeft ? styleOptions.paddingLeft : 0;
        const paddingRight =
            styleOptions && styleOptions.paddingRight ? styleOptions.paddingRight : 0;
        const availableWidth = parentWidth - paddingLeft - paddingRight;

        containers.forEach((container: Phaser.GameObjects.Container) => {
            const centeredX = paddingLeft + (availableWidth / 2 - container.getBounds().width / 2);
            container.setX(centeredX);
        });
    }

    /**
     * Here, we care about all the elements for the placement, along with spacing options
     *
     * @param parentHeight
     * @param containers
     * @param styleOptions
     */
    public centerY(
        parentHeight: number,
        containers: Phaser.GameObjects.Container[],
        styleOptions?: StyleOptions
    ) {
        let totalHeight = 0;
        const totalElementsHeight = containers
            .map((container) => container.height)
            .reduce((previous, current) => previous + current);

        const spaceBetween =
            styleOptions && styleOptions.spaceBetween ? styleOptions.spaceBetween : 0;
        const paddingTop = styleOptions && styleOptions.paddingTop ? styleOptions.paddingTop : 0;
        const paddingBottom =
            styleOptions && styleOptions.paddingBottom ? styleOptions.paddingBottom : 0;

        // console.log('total Element height: ', totalElementsHeight);
        // console.log('style options: ', styleOptions);
        // console.log('space x containers: ', spaceBetween * containers.length);
        // console.log('padding top: ', paddingTop);
        // console.log('padding bottom: ', paddingBottom);

        if (styleOptions) {
            totalHeight =
                totalElementsHeight + spaceBetween * containers.length + paddingTop + paddingBottom;
        } else {
            totalHeight = totalElementsHeight;
        }
        // console.log('parent height: ', parentHeight, ', total height: ', totalHeight);

        const firstElement = parentHeight / 2 - totalHeight / 2;
        // console.log('first Element: ', firstElement);
        containers.forEach((container: Phaser.GameObjects.Container, index: number) => {
            let xPos = firstElement + index * container.height;
            if (index > 0) {
                xPos = xPos + spaceBetween * index;
            }
            container.setY(xPos);
        });
    }
}

export class HorizontalLayout extends Layout {
    /**
     * Center elements vertically with optional padding
     *
     * @param parentHeight
     * @param containers
     * @param styleOptions
     */
    public centerY(
        parentHeight: number,
        containers: Phaser.GameObjects.Container[],
        styleOptions?: StyleOptions
    ) {
        const paddingTop = styleOptions && styleOptions.paddingTop ? styleOptions.paddingTop : 0;
        const paddingBottom =
            styleOptions && styleOptions.paddingBottom ? styleOptions.paddingBottom : 0;
        const availableHeight = parentHeight - paddingTop - paddingBottom;

        containers.forEach((container: Phaser.GameObjects.Container) => {
            const centeredY = paddingTop + (availableHeight / 2 - container.height / 2);
            container.setY(centeredY);
        });
    }

    /**
     * Here, we care about all the elements for the placement, along with spacing options
     *
     * @param parentWidth
     * @param containers
     * @param styleOptions
     */
    public centerX(
        parentWidth: number,
        containers: Phaser.GameObjects.Container[],
        styleOptions?: StyleOptions
    ) {
        let totalWidth = 0;
        const totalElementsWidth = containers
            .map((container) => container.width)
            .reduce((previous, current) => previous + current);

        const spaceBetween =
            styleOptions && styleOptions.spaceBetween ? styleOptions.spaceBetween : 0;
        const paddingLeft = styleOptions && styleOptions.paddingLeft ? styleOptions.paddingLeft : 0;
        const paddingRight =
            styleOptions && styleOptions.paddingRight ? styleOptions.paddingRight : 0;

        // console.log('parent Width: ', parentWidth);
        // console.log('total Element Width: ', totalElementsWidth);
        // console.log('style options: ', styleOptions);
        // console.log('space x containers: ', spaceBetween * containers.length);
        // console.log('padding left: ', paddingLeft);
        // console.log('padding right: ', paddingRight);

        if (styleOptions) {
            totalWidth =
                totalElementsWidth +
                spaceBetween * (containers.length - 1) +
                paddingLeft +
                paddingRight;
        } else {
            totalWidth = totalElementsWidth;
        }
        // console.log('parent Width: ', parentWidth, ', total Width: ', totalWidth);

        const firstElement = parentWidth / 2 - totalWidth / 2;
        // console.log('first Element: ', firstElement);
        containers.forEach((container: Phaser.GameObjects.Container, index: number) => {
            let xPos = firstElement + index * container.width;
            xPos = xPos + spaceBetween * index;
            container.setX(xPos);
        });
    }
}

export interface GridLayoutInfo {
    rows: number[][]; // Array of arrays, each inner array contains indices of items in that row
}

export class GridLayout extends Layout {
    public drawGrid(
        parentWidth: number,
        parentHeight: number,
        containers: Phaser.GameObjects.Container[],
        styleOptions?: StyleOptions
    ): GridLayoutInfo {
        const paddingLeft =
            styleOptions && styleOptions.paddingLeft
                ? styleOptions.paddingLeft
                : this.themeManager.getSpacing("md");
        const paddingTop =
            styleOptions && styleOptions.paddingTop
                ? styleOptions.paddingTop
                : this.themeManager.getSpacing("md");
        const paddingRight =
            styleOptions && styleOptions.paddingRight
                ? styleOptions.paddingRight
                : this.themeManager.getSpacing("md");
        const spaceBetween =
            styleOptions && styleOptions.spaceBetween
                ? styleOptions.spaceBetween
                : this.themeManager.getMenuSpacing("verticalGap");
        const horizontalGap = this.themeManager.getMenuSpacing("horizontalGap");

        // First pass: determine row structure
        const rows = this.calculateRows(
            containers,
            parentWidth,
            paddingLeft,
            paddingRight,
            horizontalGap
        );

        // Second pass: calculate column widths for vertical alignment
        const columnWidths = this.calculateColumnWidths(containers, rows);

        // Third pass: position items using fixed column widths
        let yPos = paddingTop;
        rows.forEach((row, rowIndex) => {
            const rowHeights = row.map((index) => containers[index].getBounds().height);
            const maxHeightInRow = Math.max(...rowHeights);

            let xPos = paddingLeft;
            row.forEach((itemIndex, colIndex) => {
                const container = containers[itemIndex];
                const containerHeight = container.getBounds().height;

                // Position horizontally using column width
                container.setX(xPos);

                // Center vertically within the row
                const centeredY = yPos + (maxHeightInRow - containerHeight) / 2;
                container.setY(centeredY);

                // Move to next column position
                xPos += columnWidths[colIndex] + horizontalGap;
            });

            yPos += maxHeightInRow + spaceBetween;
        });

        return { rows };
    }

    private calculateRows(
        containers: Phaser.GameObjects.Container[],
        parentWidth: number,
        paddingLeft: number,
        paddingRight: number,
        horizontalGap: number
    ): number[][] {
        const rows: number[][] = [];
        let currentRow: number[] = [];
        let xPos = paddingLeft;

        containers.forEach((container, index) => {
            const containerWidth = container.getBounds().width;

            // Check if we need to wrap to next line
            if (xPos > paddingLeft && xPos + containerWidth + paddingRight > parentWidth) {
                rows.push(currentRow);
                currentRow = [];
                xPos = paddingLeft;
            }

            currentRow.push(index);
            xPos += containerWidth + horizontalGap;
        });

        if (currentRow.length > 0) {
            rows.push(currentRow);
        }

        return rows;
    }

    private calculateColumnWidths(
        containers: Phaser.GameObjects.Container[],
        rows: number[][]
    ): number[] {
        const columnWidths: number[] = [];

        // Find maximum width for each column position
        rows.forEach((row) => {
            row.forEach((itemIndex, colIndex) => {
                const containerWidth = containers[itemIndex].getBounds().width;
                if (
                    columnWidths[colIndex] === undefined ||
                    containerWidth > columnWidths[colIndex]
                ) {
                    columnWidths[colIndex] = containerWidth;
                }
            });
        });

        return columnWidths;
    }
}
